import React, { useState } from "react";
import { addSignersBulk } from "../../api/adminApi";
import { useToast } from "../common/ToastProvider";

export default function BulkAddSigners({ documentId, onCompleted }) {
  const [signers, setSigners] = useState([
    { name: "", email: "", signOrder: 1 },
  ]);
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [saving, setSaving] = useState(false);
  const isValidEmail = (email) => {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((email || "").trim());
  };

  const toast = useToast();

  const addRow = () => {
    setSigners((prev) => [
      ...prev,
      { name: "", email: "", signOrder: prev.length + 1 },
    ]);
  };

  const removeRow = (index) => {
    const updated = signers
      .filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, signOrder: i + 1 }));
    setSigners(updated);
  };

  const update = (index, field, value) => {
    const updated = [...signers];
    updated[index][field] = value;
    setSigners(updated);

    if (field === "name" && nameError) {
      setNameError("");
    }

    if (field === "email" && emailError) {
      setEmailError("");
    }
  };

  const submit = async () => {
    setEmailError("");
    setNameError("");

    const payload = signers.map((s) => ({
      name: s.name.trim(),
      email: s.email.trim(),
      signOrder: Number(s.signOrder),
    }));

    const hasMissingName = payload.some((s) => !s.name);
    const hasMissingEmail = payload.some((s) => !s.email);
    const hasInvalidEmail = payload.some(
      (s) => s.email && !isValidEmail(s.email),
    );
    const normalizedEmails = payload
      .map((s) =>
        String(s.email || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);
    const hasDuplicateEmail =
      normalizedEmails.length !== new Set(normalizedEmails).size;

    if (
      hasMissingName ||
      hasMissingEmail ||
      hasInvalidEmail ||
      hasDuplicateEmail
    ) {
      if (hasMissingName) {
        setNameError("Name is required for all signers");
      }

      if (hasMissingEmail) {
        setEmailError("Email is required for all signers");
      } else if (hasInvalidEmail) {
        setEmailError("The email address is not valid");
      } else if (hasDuplicateEmail) {
        setEmailError("Duplicate email is not allowed");
      }

      return;
    }

    try {
      setSaving(true);
      await addSignersBulk(documentId, payload);
      toast.success("All signers added successfully");
      onCompleted?.();
      setSigners([{ name: "", email: "", signOrder: 1 }]);
    } catch (e) {
      toast.error("Failed to add signers");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="form-grid">
        {signers.map((s, i) => (
          <div
            key={i}
            className="form-grid-2 flex items-center"
            style={{
              alignItems: "end",
              border: "1px solid var(--border)",
              padding: "16px 12px",
              borderRadius: 8,
            }}
          >
            <div>
              <label className="label">
                Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                className="input"
                placeholder="Signer name"
                value={s.name}
                onChange={(e) => update(i, "name", e.target.value)}
              />
              <div style={{ height: 20 }}>
                {nameError && <div className="error">{nameError}</div>}
              </div>
            </div>

            <div>
              <label className="label">
                Email <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="email"
                className="input"
                placeholder="Signer email"
                value={s.email}
                onChange={(e) => update(i, "email", e.target.value)}
              />
              <div style={{ height: 20 }}>
                {emailError && <div className="error">{emailError}</div>}
              </div>
            </div>

            <div>
              <label className="label">
                Sign Order <span style={{ color: "red" }}>*</span>
              </label>
              <input
                className="input"
                type="number"
                value={s.signOrder}
                disabled
              />
              <div style={{ height: 20 }} />
            </div>

            <div>
              <button className="btn btn-light" onClick={() => removeRow(i)}>
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="inline-actions">
          <button className="btn btn-primary" onClick={addRow}>
            Add Signer
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save All Signers"}
          </button>
        </div>
      </div>
    </div>
  );
}
